/**
 * Product recommendations ranker.
 *
 * The PDP renders an <aside data-product-recs data-recs-config="{…}"> with any
 * merchant-curated picks already in the grid. This script fetches candidate
 * cards (rendered server-side by sections/product-recommendations.liquid) from
 * several sources and fills the grid by relevance tier.
 *
 * Every candidate <li> carries: data-id, data-dept (schoolwear|main),
 * data-school (handleized key or ''), data-type, data-vendor, data-available.
 *
 * Schoolwear product:
 *   1. same school (exact key)            — school collection / title search
 *   2. same school (key prefix match)     — "brookfield" ↔ "brookfield-post"
 *   3. complementary (S&D)                — schoolwear, generic or same school
 *   4. fill-in collections, in order      — school essentials, sports bags, sports…
 *   5. Shopify "related"                  — schoolwear, generic only
 *   Never: another school's uniform, menswear, sold out.
 *   Same-school items are capped so a few essentials always get through.
 *
 * Main (menswear) product:
 *   1. complementary (S&D)  2. Shopify "related"  3. fill-in collections
 *   Never: schoolwear, sold out. Colourway siblings (same title before the
 *   " - Colour" suffix) are capped at half the slots so it isn't one polo ×8.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-product-recs]');
  if (!root || !('fetch' in window)) return;

  var cfg;
  try {
    cfg = JSON.parse(root.getAttribute('data-recs-config'));
  } catch (e) {
    return;
  }

  var grid = root.querySelector('[data-recs-grid]');
  var heading = root.querySelector('[data-recs-heading]');
  if (!grid) return;

  var limit = parseInt(cfg.limit, 10) || 8;
  var isSchool = cfg.dept === 'schoolwear';
  var mySchool = cfg.school || '';
  var seen = {};
  seen[String(cfg.productId)] = true;

  // Curated picks rendered by Liquid already occupy slots.
  var chosen = [];
  Array.prototype.forEach.call(grid.children, function (li) {
    var id = li.getAttribute('data-id');
    if (id) seen[id] = true;
    chosen.push(li);
  });

  function sectionUrl(base, params) {
    var joiner = base.indexOf('?') === -1 ? '?' : '&';
    return base + joiner + params;
  }

  function fetchCards(url) {
    return fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.prototype.slice.call(doc.querySelectorAll('.pdp-recs-source li[data-id]'));
      })
      .catch(function () {
        return [];
      });
  }

  function meta(li) {
    return {
      id: li.getAttribute('data-id'),
      dept: li.getAttribute('data-dept') || 'main',
      school: li.getAttribute('data-school') || '',
      base: li.getAttribute('data-base') || '',
      available: li.getAttribute('data-available') !== 'false'
    };
  }

  function schoolMatch(a, b) {
    if (!a || !b) return 'none';
    if (a === b) return 'exact';
    if (a.length >= 4 && b.length >= 4 && (a.indexOf(b + '-') === 0 || b.indexOf(a + '-') === 0)) return 'prefix';
    return 'none';
  }

  // Is this candidate allowed at all for the current product?
  function allowed(m, source) {
    if (!m.available) return false;
    if (isSchool) {
      // Another school's uniform is never relevant.
      if (m.dept === 'schoolwear' && m.school && schoolMatch(mySchool, m.school) === 'none') return false;
      // Menswear only leaks in through collections the merchant chose deliberately.
      if (m.dept === 'main' && (source !== 'fallback' || cfg.fallbackStrict)) return false;
      return true;
    }
    return m.dept !== 'schoolwear';
  }

  function take(list, source, max, predicate) {
    var out = [];
    for (var i = 0; i < list.length && out.length < max; i++) {
      var li = list[i];
      var m = meta(li);
      if (!m.id || seen[m.id]) continue;
      if (!allowed(m, source)) continue;
      if (predicate && !predicate(m)) continue;
      seen[m.id] = true;
      out.push(li);
    }
    return out;
  }

  var requests = {
    school: cfg.schoolUrl ? fetchCards(cfg.schoolUrl) : Promise.resolve([]),
    complementary: fetchCards(sectionUrl(cfg.recsUrl, 'section_id=product-recommendations&product_id=' + cfg.productId + '&limit=10&intent=complementary')),
    related: fetchCards(sectionUrl(cfg.recsUrl, 'section_id=product-recommendations&product_id=' + cfg.productId + '&limit=10&intent=related')),
    fallback: Promise.all(
      (cfg.fallback || []).filter(Boolean).map(function (handle) {
        return fetchCards(sectionUrl(cfg.collectionsRoot + '/' + handle, 'section_id=product-recommendations'));
      })
    ).then(function (lists) {
      var flat = [].concat.apply([], lists);
      // Default fill-ins aren't curated, so rotate them per product — every
      // school page shouldn't open with the same two gym bags.
      if (cfg.fallbackStrict && flat.length > 1) {
        var offset = (parseInt(cfg.productId, 10) || 0) % flat.length;
        flat = flat.slice(offset).concat(flat.slice(0, offset));
      }
      return flat;
    })
  };

  Promise.all([requests.school, requests.complementary, requests.related, requests.fallback]).then(function (res) {
    var school = res[0], complementary = res[1], related = res[2], fallback = res[3];
    var room = function () { return limit - chosen.length; };
    var picked = [];
    var sameSchoolCount = 0;

    if (isSchool) {
      // Same-school first, from the school source then Shopify's list —
      // but leave room for a few essentials when we have them.
      var others = [].concat(complementary, fallback, related);
      var reserve = Math.min(Math.ceil(limit / 3), others.length);
      var schoolMax = Math.max(0, room() - reserve);
      var pool = [].concat(school, complementary, related, fallback);

      var exact = take(pool, 'school', schoolMax, function (m) { return schoolMatch(mySchool, m.school) === 'exact'; });
      var prefix = take(pool, 'school', schoolMax - exact.length, function (m) { return schoolMatch(mySchool, m.school) === 'prefix'; });
      picked = picked.concat(exact, prefix);
      sameSchoolCount = picked.length;

      picked = picked.concat(take(complementary, 'recs', room() - picked.length, function (m) { return m.dept === 'schoolwear'; }));
      picked = picked.concat(take(fallback, 'fallback', room() - picked.length));
      picked = picked.concat(take(related, 'recs', room() - picked.length, function (m) { return m.dept === 'schoolwear' && !m.school; }));
      // Still short? Let remaining same-school items through after all.
      picked = picked.concat(take(pool, 'school', room() - picked.length, function (m) { return schoolMatch(mySchool, m.school) !== 'none'; }));
    } else {
      var sibMax = Math.ceil(limit / 2), sibs = 0;
      var capSiblings = function (m) {
        if (cfg.titleBase && m.base === cfg.titleBase) {
          if (sibs >= sibMax) return false;
          sibs++;
        }
        return true;
      };
      picked = picked.concat(take(complementary, 'recs', room(), capSiblings));
      picked = picked.concat(take(related, 'recs', room() - picked.length, capSiblings));
      picked = picked.concat(take(fallback, 'fallback', room() - picked.length, capSiblings));
      // Short? Let the remaining colourways through.
      picked = picked.concat(take([].concat(complementary, related, fallback), 'recs', room() - picked.length));
    }

    if (!picked.length && !chosen.length) return;

    var frag = document.createDocumentFragment();
    picked.forEach(function (li) {
      li.removeAttribute('class');
      frag.appendChild(li);
    });
    grid.appendChild(frag);

    if (heading && isSchool && sameSchoolCount > 0 && cfg.headingSchool && cfg.schoolName) {
      heading.textContent = cfg.headingSchool.replace('{school}', cfg.schoolName);
    }

    root.removeAttribute('hidden');
    root.dispatchEvent(new CustomEvent('recs:loaded', { bubbles: true, detail: { count: grid.children.length } }));
  });
})();
